import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import scanApi from '../services/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [quotaInfo, setQuotaInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const refreshUserInfo = useCallback(async () => {
    try {
      setLoading(true);
      const userData = await scanApi.getCurrentUser();
      setUser(userData);
      setQuotaInfo(userData.quotaInfo);
      setIsAuthenticated(!!userData);
    } catch (error) {
      console.error('Error updating user info:', error);
      setIsAuthenticated(false);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const login = async (credentials) => {
    try {
      let response;
      
      if (credentials.loginType === 'google') {
        console.log('Tentativo di login con Google:', {
          hasToken: !!credentials.googleToken,
          tokenLength: credentials.googleToken?.length
        });
        
        response = await scanApi.loginWithGoogle(credentials.googleToken);
        
        console.log('Risposta login Google:', {
          success: !!response,
          hasToken: !!response?.token,
          requiresApproval: response?.requiresApproval
        });
      } else if (credentials.loginType === 'email') {
        if (credentials.step === 'request-pin') {
          response = await scanApi.requestEmailPin(credentials.email);
        } else {
          response = await scanApi.verifyEmailPin(credentials.email, credentials.pin);
        }
      }

      if (response?.token) {
        localStorage.setItem('token', response.token);
        await refreshUserInfo();
        
        // Aggiorna lo stato di autenticazione
        setIsAuthenticated(true);
        
        // Aggiorna le informazioni sulla quota se disponibili
        if (response.quotaInfo) {
          setQuotaInfo(response.quotaInfo);
        }
      }

      return response;
    } catch (error) {
      console.error('Errore durante il login:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });

      // Gestione specifica degli errori
      if (error.response?.status === 403 && error.response.data?.requiresApproval) {
        throw new Error('ACCOUNT_PENDING_APPROVAL');
      }

      if (error.response?.status === 400) {
        throw new Error(error.response.data?.error || 'Errore di validazione');
      }

      if (error.response?.status === 401) {
        throw new Error('Credenziali non valide');
      }

      throw error;
    }
  };

  const logout = async () => {
    try {
      await scanApi.logout();
      localStorage.removeItem('auth_token');
      setUser(null);
      setIsAuthenticated(false);
      window.location.href = '/login';
    } catch (error) {
      console.error('Errore durante il logout:', error);
      throw error;
    }
  };

  const register = async (userData) => {
    try {
      const response = await scanApi.register(userData);
      return response;
    } catch (error) {
      console.error('Errore durante la registrazione:', error);
      throw error;
    }
  };

  useEffect(() => {
    // Initial load
    refreshUserInfo();

    // Listen for auth updates
    const handleAuthUpdate = () => refreshUserInfo();
    window.addEventListener('authUpdate', handleAuthUpdate);

    // Listen for quota updates
    const handleQuotaUpdate = (event) => {
      setQuotaInfo(event.detail);
    };
    window.addEventListener('quotaUpdate', handleQuotaUpdate);

    // Listen for auth errors
    const handleAuthError = () => {
      setUser(null);
      setIsAuthenticated(false);
      localStorage.removeItem('auth_token');
    };
    window.addEventListener('authError', handleAuthError);

    return () => {
      window.removeEventListener('authUpdate', handleAuthUpdate);
      window.removeEventListener('quotaUpdate', handleQuotaUpdate);
      window.removeEventListener('authError', handleAuthError);
    };
  }, [refreshUserInfo]);

  const value = {
    user,
    quotaInfo,
    loading,
    refreshUserInfo,
    isAuthenticated,
    setIsAuthenticated,
    login,
    logout,
    register
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
